import { observable, IObservableArray, computed } from "mobx";
import { copy, copyArray, mapArray, sort } from "../utils/data";
import { EmptyId, EmptyESId, PositionLevel, JobType, EdType, RefType, StatusFlag, ProfileViewState, ExperienceType} from "./enums";
import { CCLCReq, OpportunityFit } from "./opportunity";

export class Address {
  @observable street1: string;
  @observable street2: string;
  @observable city: string;
  @observable province: string;
  @observable state: string;
  @observable zip: string;

  constructor(addr?: Address) {
    this.street1 = this.street2 = this.city = this.province = this.state = this.zip = "";
    if (addr) {
      copy(addr, this);
    }
  }
}

export class Referral {
  @observable regionIdHash: string;
  @observable sourceIdHash: string;
  constructor(r: Referral) {
    this.regionIdHash = r.regionIdHash || "";
    this.sourceIdHash = r.sourceIdHash || "";
  }
}

export class Demographics {
  @observable address: Address;
  @observable gender: string;
  @observable dob: Date;
  @observable referralId: string;
  @observable referralSrc: string;
  @observable ethnicityId: number;
  @observable ethnicity: string;
  @observable veteran: boolean;
  @observable disabledVet: boolean;
  @observable phone: string;
  @observable statusId: number;
  @observable status: string;
  @observable notifications: number;
  @observable contact: number;
  @observable age: number;
  @observable firstName: string;
  @observable lastName: string;
  @observable emailAddress: string;
  @observable contactFlag: boolean;
  @observable eduDist: number;
  @observable vetStatus: number;
  @observable referrals: Array<Referral>;

  constructor(demo: Demographics) {
    copy(demo, this, ["address","dob", "referrals"]);
    this.address = new Address(demo.address);
    this.referrals = new Array<Referral>();
    if (demo.dob) {
      this.dob = new Date(demo.dob.valueOf());
    }
    if (demo.referrals) {
      copyArray(demo.referrals, this.referrals, (r: Referral) => { return new Referral(r); });
    }
    this.contactFlag = this.contact === 1;
  }
}

export class SkillFit {
  @observable skill: string;
  @observable level: number;
  @observable viewed: boolean;

  constructor(sf?: SkillFit) {
    if (sf) {
      copy(sf, this);
    }
  }
}

export class InitSkillLevel {
  @observable skillId: string;
  @observable initCompetence: number;

  constructor(sl?: InitSkillLevel) {
    this.skillId = EmptyESId;
    this.initCompetence = 0.0;
    if (sl) {
      copy(sl, this);
    }
  }
}

export class ProfilePrereq {
  // not sure if this is valid?
  constructor(p: ProfilePrereq) {
    copy(p, this);
  }
}

export class SavedOpportunity {
  @observable opportunityId: string;
  @observable opportunityName: string;
  @observable fit: number;
  @observable status: StatusFlag;
  @observable scored: string; // Date
  @observable skillFit: Array<SkillFit>;
  @observable completed: boolean;
  @observable prerequisites: Array<ProfilePrereq>;

  constructor(so?: SavedOpportunity) {
    this.opportunityId = EmptyId;
    this.opportunityName = "";
    this.fit = 0;
    this.status = StatusFlag.Active;
    this.scored = null;
    this.skillFit = new Array<SkillFit>();
    this.completed = false;
    this.prerequisites = new Array<ProfilePrereq>();
    if (so) {
      copy(so, this, ["skillFit", "prerequisites"]);
      this.skillFit = new Array<SkillFit>();
      copyArray(so.skillFit, this.skillFit, (sf) => { return new SkillFit(sf); });
      this.prerequisites = new Array<ProfilePrereq>();
      copyArray(so.prerequisites, this.prerequisites, (p) => { return new ProfilePrereq(p); });
    }
  }
  mapFromFit = (opfit: OpportunityFit): void => {
      this.opportunityId = opfit.opportunityId;
      this.opportunityName = opfit.opportunityName;
      this.fit = opfit.fit;
      this.status = opfit.status;
      copyArray(opfit.skillFit, this.skillFit, (sf: SkillFit) => { return new SkillFit(sf); });
      this.completed = opfit.completed;
  }
  getScore = (skillId: string): number => {
    const sf = this.skillFit.find((s: SkillFit): boolean => {
      return s.skill === skillId;
    });
    return sf ? sf.level : 0;
  }
}

export class ExperienceSkill {
  @observable skillId: string;
  @observable used: number;
  @observable level: number;

  constructor(es?: ExperienceSkill) {
    this.skillId = "";
    this.used = 0;
    this.level = 0;
    if (es) {
      copy(es, this);
    }
  }
}

export interface IExperience {
  idHash: string;
}

export interface IReference {
    refName: string;
    refTitle: string;
    refPhone: string;
    refContact: string;
}

export interface IDescription {
  desc: string;
}

export interface ISkillSource {
  skills: Array<ExperienceSkill>;
}

export class WorkExperience implements IExperience, IReference, IDescription, ISkillSource {
  @observable position: string;
  @observable refPositionId: string;
  @observable employer: string;
  @observable service: number;
  @observable serviceType: number;
  @observable rank: number;
  @observable mos: string;
  @observable expType: JobType;
  @observable refName: string;
  @observable refTitle: string;
  @observable refContact: string;
  @observable refPhone: string; // Need to make reference their own collection
  @observable skills: Array<ExperienceSkill>;
  @observable fromMon: number;
  @observable fromYear: number;
  @observable toMon: number;
  @observable toYear: number;
  @observable active: boolean;
  @observable idHash: string;
  @observable desc: string;
  @observable level: PositionLevel;
  @observable isNew: boolean;

  constructor(we?: WorkExperience) {
    this.position = this.refPositionId = this.employer = this.mos = this.refName = this.refTitle = this.refContact = this.refPhone = this.desc = "";
    this.service = this.serviceType = 0;
    this.rank = 0;
    this.level = PositionLevel.NotSpecified;
    this.expType = 0;
    this.idHash = EmptyId;
    this.skills = new Array<ExperienceSkill>();
    var dt = new Date();
    this.fromMon = dt.getMonth() + 1;
    this.fromYear = dt.getFullYear();
    this.toMon = dt.getMonth() + 1;
    this.toYear = dt.getFullYear();
    this.active = true;
    this.isNew = true;
    if (we) {
      copy(we, this, ["skills"]);
      copyArray(we.skills, this.skills, (s) => { return new ExperienceSkill(s); });
      this.isNew = false;
    }
  }
}

export class EducationExperience implements IExperience, IReference, IDescription, ISkillSource {
  @observable name: string;
  @observable courseTitle: string;
  @observable edType: EdType;
  @observable degTypeId: number;
  @observable courseId: string;
  @observable major: string;
  @observable minor: string;
  @observable online: boolean;
  @observable forCredit: boolean;
  @observable grad: boolean;
  @observable gpa: number;
  @observable grade: string;
  @observable level: number;
  @observable refName: string;
  @observable refTitle: string;
  @observable refContact: string;
  @observable refPhone: string;
  @observable skills: Array<ExperienceSkill>;
  @observable fromMon: number;
  @observable fromYear: number;
  @observable toMon: number;
  @observable toYear: number;
  @observable active: boolean;
  @observable idHash: string;
  @observable desc: string;
  @observable isNew: boolean;

  constructor(e?: EducationExperience) {
    this.idHash = EmptyId;
    this.name = this.courseTitle = this.courseId = this.major = this.minor = this.grade = this.refName = this.refTitle = this.refContact = this.refPhone =  this.desc = "";
    this.edType = EdType.Unknown;
    this.degTypeId = this.gpa = this.level = 0;
    const now:Date = new Date();
    this.fromMon = this.toMon = now.getMonth() + 1;
    this.fromYear = this.toYear = now.getFullYear();
    this.online = this.forCredit = this.grad = this.active = false;
    this.isNew = true;
    this.skills = new Array<ExperienceSkill>();
    if (e) {
      copy(e, this, ["skills"]);
      copyArray(e.skills, this.skills, (s) => { return new ExperienceSkill(s); });
      this.isNew = false;
    }
  }
  @computed get passed(): boolean {
    if (this.edType === EdType.Course) {
      switch (this.grade) {
        case "A":
        case "B":
        case "C":
        case "P":
          return true;
        default:
          return false;
      }
    } else {
      return false;
    }
  }
}

export class ActivityExperience implements IExperience, IReference, IDescription, ISkillSource {
  @observable org: string;
  @observable role: string;
  @observable typeId: string;
  @observable actType: number;
  @observable activityType: string;
  @observable refName: string;
  @observable refTitle: string;
  @observable refContact: string;
  @observable refPhone: string;
  @observable level: number;
  @observable refUrl: string;
  @observable skills: Array<ExperienceSkill>;
  @observable fromMon: number;
  @observable fromYear: number;
  @observable toMon: number;
  @observable toYear: number;
  @observable active: boolean;
  @observable idHash: string;
  @observable desc: string;
  @observable isNew: boolean;

  constructor(a?: ActivityExperience) {
    this.idHash = EmptyId;
    this.org = this.role = this.typeId = this.activityType = this.refName = this.refTitle = this.refContact = this.refPhone = this.refUrl = this.desc = "";
    this.actType = this.level = 0;
    const now: Date = new Date();
    this.fromMon = this.toMon = now.getMonth() + 1;
    this.fromYear = this.toYear = now.getFullYear();
    this.active = false;
    this.skills = new Array<ExperienceSkill>();
    this.isNew = true;
    if (a) {
      copy(a, this, ["skills"]);
      copyArray(a.skills, this.skills, (s) => { return new ExperienceSkill(s); });
      this.isNew = false;
    }
  }
}

export class CCLCExperience implements IExperience, IDescription {
  @observable cclcId: string;
  @observable type: RefType;
  @observable issuer: string;
  @observable number: string;
  @observable score: number;
  @observable expired: boolean;
  @observable received?: Date;
  @observable expires?: Date;
  @observable title: string;
  @observable fieldMask: number;
  @observable reqFieldMask: number;
  @observable skills: Array<SkillFit>;
  @observable fromMon: number;
  @observable fromYear: number;
  @observable toMon: number;
  @observable toYear: number;
  @observable active: boolean;
  @observable idHash: string;
  @observable desc: string;
  @observable isNew: boolean;
  @observable cclcRef: CCLCReq;

  constructor(cclc?: CCLCExperience) {
    this.idHash = EmptyId;
    this.issuer = this.number = this.title = this.cclcId = this.desc = "";
    this.type = RefType.NotSpecified;
    this.cclcRef = null;
    this.score = 0;
    this.received = this.expires = null;
    this.expired = this.active = false;
    this.fromMon = this.fromYear = this.toMon = this.toYear = -1;
    this.fieldMask = this.reqFieldMask = 0;
    this.skills = new Array<SkillFit>();
    this.isNew = true;

    if (cclc) {
      copy(cclc, this, ["skills", "received", "expires"]);
      if (cclc.received) {
        this.received = new Date(cclc.received.valueOf());
      } 
      if (cclc.expires) {
        this.expires = new Date(cclc.expires.valueOf());
      }
      this.skills = new Array<SkillFit>();
      copyArray(cclc.skills, this.skills, (s) => { return new SkillFit(s); });
      this.isNew = false;
    }
  }
}

export class Language {
  @observable langId: number;
  @observable lang: string;
  @observable proficiency: number;
  @observable isNew: boolean;

  constructor(lang?: Language) {
    this.langId = this.proficiency = 0;
    this.lang = "";
    this.isNew = true;
    if (lang) {
      copy(lang, this);
      this.isNew = false;
    }
  }
}

export class ProfileSkill{
  @observable keywords: Array<string>;
  @observable title: string;
  @observable description: string;
  @observable use: string;
  @observable learn: string;
  @observable validate: string;
  @observable correlations: Array<number>;
  @observable idHash: string;

  constructor(ps?: ProfileSkill) {
    this.keywords = new Array<string>();
    this.title = this.description = this.use = this.learn = this.validate = this.idHash = "";
    this.correlations = new Array<number>();

    if (ps) {
      copy(ps, this, ["keywords", "correlations"]);
      mapArray(ps.keywords, this.keywords);
      mapArray(ps.correlations, this.correlations);
    }
  }
}

export class DeleteExperience {
    @observable idHash: string;
    @observable type: ExperienceType;
    @observable name: string;

    constructor(id: string, type: ExperienceType, name: string) {
        this.idHash = id;
        this.type = type;
        this.name = name;
    }
}

export class Profile {
  @observable uid: string;
  @observable demographics: Demographics;
  @observable savedOps: Array<SavedOpportunity>;
  @observable jobs: Array<WorkExperience>;
  @observable educations: Array<EducationExperience>;
  @observable activities: Array<ActivityExperience>;
  @observable coursesViewed: Array<string>;
  @observable cclcs: Array<CCLCExperience>;
  @observable selfCerts: Array<string>;
  @observable languages: Array<Language>;
  @observable skills: Array<ProfileSkill>;
  @observable initSkillLevels: Array<InitSkillLevel>;
  @observable resumeId: string;
  @observable resumeDoc: string;
  @observable resumeUpload: Date;
  @observable idHash: string;
  @observable allExperienceSkills: Array<ExperienceSkill>;

  constructor(p: Profile) {
    this.uid = p.uid;
    this.idHash = p.idHash;
    this.resumeId = p.resumeId;
    this.resumeDoc = p.resumeDoc;
    if (p.resumeUpload) {
      this.resumeUpload = new Date(p.resumeUpload.valueOf());
    }
    this.demographics = new Demographics(p.demographics);
    this.savedOps = new Array<SavedOpportunity>();
    copyArray(p.savedOps, this.savedOps, (so) => { return new SavedOpportunity(so); });
    this.jobs = new Array<WorkExperience>();
    copyArray(p.jobs, this.jobs, (we) => { return new WorkExperience(we); });
    this.educations = new Array<EducationExperience>();
    copyArray(p.educations, this.educations, (ee) => { return new EducationExperience(ee); });
    this.activities = new Array<ActivityExperience>();
    copyArray(p.activities, this.activities, (ae) => { return new ActivityExperience(ae); });
    this.coursesViewed = new Array<string>();
    mapArray(p.coursesViewed, this.coursesViewed);
    this.cclcs = new Array<CCLCExperience>();
    copyArray(p.cclcs, this.cclcs, (ce) => { return new CCLCExperience(ce); });
    this.selfCerts = new Array<string>();
    mapArray(p.selfCerts, this.selfCerts);
    this.languages = new Array<Language>();
    copyArray(p.languages, this.languages, (l) => { return new Language(l); });
    this.skills = new Array<ProfileSkill>();
    copyArray(p.skills, this.skills, (ps) => { return new ProfileSkill(ps); });
    this.initSkillLevels = new Array<InitSkillLevel>();
    copyArray(p.initSkillLevels, this.initSkillLevels, (sl) => { return new InitSkillLevel(sl); });
  }

  getWorkExperience = (eId: string): WorkExperience => {
    const ex = this.jobs.find((e: WorkExperience): boolean => {
      return e.idHash === eId;
    });
    return ex ? ex : null;
  }
  getActivityExperience = (eId: string): ActivityExperience => {
    const ex = this.activities.find((e: ActivityExperience): boolean => {
      return e.idHash === eId;
    });
    return ex ? ex : null;
  }
  getEducationExperience = (eId: string): EducationExperience => {
    const ex = this.educations.find((e: EducationExperience): boolean => {
      return e.idHash === eId;
    });
    return ex ? ex : null;
  }
  getCCLCExperience = (eId: string): CCLCExperience => {
    const ex = this.cclcs.find((e: CCLCExperience): boolean => {
      return e.idHash === eId;
    });
    return ex ? ex : null;
  }

  getSkillName = (skId: string): string => {
    const skill = this.skills.find((ps: ProfileSkill): boolean => {
      return ps.idHash === skId;
    });
    return skill ? skill.title : null;
  }
}


// WEBPACK FOOTER //
// ./src/models/profile.ts